import React, { useState } from "react";
import {
    Loader2, Search, AlertTriangle, Trash2, ShieldCheck, User, IdCard,
    Calendar,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin tool — look up a talent passport by email + delete it on request.
 *
 * Backing endpoints:
 *   - GET    /admin/passport-by-email?email=…  (see routes/admin_ops.py)
 *   - DELETE /admin/passports/{email}          (platform_admin only)
 *
 * Intended workflow:
 *   1. Support ticket comes in: "Please delete my passport, I can't sign in."
 *   2. Admin pastes the requester's email here.
 *   3. Confirms passport exists + inspects last-updated / trust score to
 *      spot obvious bad-faith deletion requests before pulling the trigger.
 *   4. Types the email again to confirm, hits Delete — audit_log.action =
 *      passport.delete.admin is stamped with the platform_admin's identity.
 */
export default function AdminPassportLookup() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [found, setFound] = useState(null);   // {exists, passport} | null
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmText, setConfirmText] = useState("");

    const doLookup = async (e) => {
        e?.preventDefault();
        const target = email.trim().toLowerCase();
        if (!target || !target.includes("@")) {
            toast.error("Enter a valid email address.");
            return;
        }
        try {
            setLoading(true); setFound(null);
            const { data } = await api.get(`/admin/passport-by-email`, { params: { email: target } });
            setFound(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const doDelete = async () => {
        try {
            setDeleting(true);
            const target = (found?.email || email).trim().toLowerCase();
            const { data } = await api.delete(`/admin/passports/${encodeURIComponent(target)}`);
            toast.success(data?.deleted
                ? `Passport for ${target} deleted. Audit stamped.`
                : `No passport row existed for ${target}. Nothing to delete.`);
            setDeleteOpen(false); setConfirmText("");
            // Refresh the lookup card so it flips to "no passport"
            await doLookup();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="container-page py-8 space-y-6" data-testid="admin-passport-lookup">
            <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold">Compliance</div>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Passport Lookup
                </h1>
                <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                    GDPR / support tool for deleting a talent&apos;s passport on their behalf when they
                    can&apos;t sign in themselves. Every action is stamped in the audit log with your
                    platform_admin identity.
                </p>
            </div>

            <form
                onSubmit={doLookup}
                className="card-soft p-4 flex items-end gap-3 flex-wrap"
                data-testid="passport-lookup-form"
            >
                <div className="flex-1 min-w-[260px]">
                    <label htmlFor="pl-email" className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                        Talent email
                    </label>
                    <Input
                        id="pl-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="talent@example.com"
                        data-testid="passport-lookup-email"
                        autoFocus
                        className="mt-1"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading || !email.trim()}
                    className="btn-green text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid="passport-lookup-submit"
                >
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                    Look up
                </button>
            </form>

            {found && (
                <div className="card-soft p-5" data-testid="passport-lookup-result">
                    {!found.exists ? (
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <div className="w-10 h-10 rounded-xl bg-muted/40 text-muted-foreground flex items-center justify-center">
                                <User size={16} />
                            </div>
                            <div>
                                <p className="text-foreground font-medium">No passport found for <span className="font-mono">{found.email}</span>.</p>
                                <p className="mt-0.5 text-xs">
                                    Either the email doesn&apos;t match any talent user, or that user has never
                                    completed a passport. There&apos;s nothing to delete.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0">
                                <IdCard size={20} />
                            </div>
                            <div className="flex-1 min-w-[260px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-heading font-semibold text-lg text-foreground">
                                        {found.passport?.name || found.email}
                                    </h3>
                                    {found.passport?.trust_score != null && (
                                        <Badge variant="outline" className="text-[10px]" data-testid="passport-lookup-trust">
                                            <ShieldCheck size={10} className="mr-1" /> Verified {found.passport.trust_score}
                                        </Badge>
                                    )}
                                    <Badge variant="outline" className="text-[10px]">
                                        <Calendar size={10} className="mr-1" /> Updated {(found.passport?.updated_at || found.passport?.created_at || "").slice(0, 10) || "—"}
                                    </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 font-mono">{found.email}</p>
                                {found.passport?.ai_summary && (
                                    <p className="text-sm text-foreground/80 mt-2 line-clamp-3">{found.passport.ai_summary}</p>
                                )}
                            </div>
                            <button
                                onClick={() => { setDeleteOpen(true); setConfirmText(""); }}
                                data-testid="passport-lookup-delete-open"
                                className="text-xs px-3 py-2 rounded border border-rose-500/40 text-rose-600 hover:bg-rose-500/10 inline-flex items-center gap-1.5 shrink-0"
                            >
                                <Trash2 size={12} /> Delete this passport
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Confirm dialog — same "type-to-confirm" pattern as the talent self-serve. */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent data-testid="passport-lookup-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-600">
                            <AlertTriangle size={16} /> Delete this passport?
                        </DialogTitle>
                        <DialogDescription className="pt-1 space-y-2">
                            <p>You&apos;re about to permanently delete the talent passport for <b className="font-mono">{found?.email}</b>.</p>
                            <p className="text-xs">Type the email again to confirm. This is stamped in the audit log with your identity.</p>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={found?.email}
                            data-testid="passport-lookup-confirm-input"
                            autoFocus
                            className="font-mono"
                        />
                    </div>
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => { setDeleteOpen(false); setConfirmText(""); }}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="passport-lookup-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={doDelete}
                            disabled={deleting || confirmText.trim().toLowerCase() !== (found?.email || "").toLowerCase()}
                            className="text-sm px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 inline-flex items-center gap-2"
                            data-testid="passport-lookup-submit-delete"
                        >
                            {deleting ? <><Loader2 size={13} className="animate-spin" /> Deleting…</> : <><Trash2 size={13} /> Delete permanently</>}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
